/**
 * Secure Logger Utility
 * Sanitizes sensitive data from logs in production
 */

import { config } from './config';

interface LogLevel {
  DEBUG: 'debug';
  INFO: 'info';
  WARN: 'warn';
  ERROR: 'error';
}

/**
 * Sensitive fields that should be redacted in logs
 */
const SENSITIVE_FIELDS = [
  'password',
  'pin',
  'apiKey',
  'api_key',
  'secret',
  'token',
  'authToken',
  'accessToken',
  'refreshToken',
  'authUserId',
  'auth_user_id',
  'cardNumber',
  'card_number',
  'cvv',
  'cvc',
  'expiry',
  'expiryDate',
  'ssn',
  'socialSecurityNumber',
  'creditCard',
  'credit_card',
  'paymentMethod',
  'payment_method',
  'bankAccount',
  'bank_account',
  'accountNumber',
  'account_number',
  'routingNumber',
  'routing_number',
];

/**
 * Redact sensitive fields from an object
 */
function redactSensitiveData(obj: any, depth: number = 0): any {
  // Prevent infinite recursion
  if (depth > 10) {
    return '[Max Depth Reached]';
  }

  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => redactSensitiveData(item, depth + 1));
  }

  // Handle objects
  if (typeof obj === 'object') {
    const redacted: any = {};
    for (const key in obj) {
      const lowerKey = key.toLowerCase();
      const isSensitive = SENSITIVE_FIELDS.some(field => 
        lowerKey.includes(field.toLowerCase())
      );

      if (isSensitive) {
        redacted[key] = '[REDACTED]';
      } else {
        redacted[key] = redactSensitiveData(obj[key], depth + 1);
      }
    }
    return redacted;
  }

  // Handle strings that might contain sensitive data
  if (typeof obj === 'string') {
    // Check if string looks like a token, API key, or password
    if (obj.length > 20 && /^[A-Za-z0-9_-]+$/.test(obj)) {
      // Might be a token or API key - redact if in production
      if (config.isProduction) {
        return '[REDACTED]';
      }
    }
  }

  return obj;
}

/**
 * Sanitize log arguments
 */
function sanitizeLogArgs(args: any[]): any[] {
  if (!config.isProduction) {
    // In development, log everything
    return args;
  }

  // In production, redact sensitive data
  return args.map(arg => {
    if (typeof arg === 'object' && arg !== null) {
      return redactSensitiveData(arg);
    }
    return arg;
  });
}

/**
 * Secure logger that redacts sensitive information in production
 */
export const logger = {
  debug: (...args: any[]) => {
    if (config.isDevelopment) {
      const sanitized = sanitizeLogArgs(args);
      console.debug(...sanitized);
    }
  },

  info: (...args: any[]) => {
    const sanitized = sanitizeLogArgs(args);
    console.info(...sanitized);
  },

  log: (...args: any[]) => {
    const sanitized = sanitizeLogArgs(args);
    console.log(...sanitized);
  },

  warn: (...args: any[]) => {
    const sanitized = sanitizeLogArgs(args);
    console.warn(...sanitized);
  },

  error: (...args: any[]) => {
    // Errors are always logged, but sensitive data is redacted
    const sanitized = sanitizeLogArgs(args);
    console.error(...sanitized);
  },

  /**
   * Log payment transaction (sanitizes payment details)
   */
  payment: (message: string, data?: any) => {
    if (data) {
      const sanitized = {
        orderId: data.orderId,
        amount: data.amount,
        method: data.method,
        transactionId: data.transactionId ? '[REDACTED]' : undefined,
        terminalId: data.terminalId,
        timestamp: data.timestamp,
        // Redact all other fields
      };
      logger.info(`💳 ${message}`, sanitized);
    } else {
      logger.info(`💳 ${message}`);
    }
  },

  /**
   * Log authentication events (sanitizes auth data)
   */
  auth: (message: string, data?: any) => {
    if (data) {
      const sanitized = {
        userId: data.userId,
        email: data.email ? data.email.replace(/(.{2})(.*)(@.*)/, '$1***$3') : undefined,
        success: data.success,
        // Redact all other fields
      };
      logger.info(`🔐 ${message}`, sanitized);
    } else {
      logger.info(`🔐 ${message}`);
    }
  },
};

export default logger;

